import React, { useMemo, useState } from "react";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import "./SiteStudyAssignmentPanel.scss";
export const getStudyDisplayNumber = (row) =>
  String(row?.StudyNumberFormatted || row?.StudyNumber || "").trim();

export const getStudyRowId = (row) =>
  String(
    row?.StudyId || row?.studyId || row?.StudyID || row?.StudyIdRef || "",
  ).trim();

export const studyHasSchedule = (row) =>
  String(row?.StudySetupStatus || "") === "1" ||
  Boolean(row?.StudyScheduleId);

export const getStudyScheduleStatus = (row) =>
  row?.ScheduleStatus ||
  (studyHasSchedule(row) ? "Scheduled" : "Not Scheduled");

export const filterStudyRows = (items, searchTerm) => {
  const query = String(searchTerm || "").trim().toLowerCase();
  if (!query) return items;

  return items.filter((row) =>
    [
      row.StudyName,
      row.StudyNumber,
      row.StudyNumberFormatted,
      row.StudyStatus,
      row.PIName,
      row.SponsorName,
      row.ScheduleStatus,
      row.StudyScheduleId,
      row.StudySchVersion,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query),
  );
};

export const StudyRowSummary = ({ row, showScheduleStatus = false }) => {
  const studyNumber = getStudyDisplayNumber(row);
  const scheduleStatus = getStudyScheduleStatus(row);
  const scheduled = studyHasSchedule(row);

  return (
    <p className="site-study-row__summary" title={row?.StudyName || ""}>
      <span className="site-study-row__name">{row?.StudyName || "Unknown"}</span>
      {studyNumber ? (
        <span className="site-study-row__sep"> · {studyNumber}</span>
      ) : null}
      {showScheduleStatus ? (
        <span
          className={`site-study-row__schedule site-study-row__schedule--${
            scheduled ? "yes" : "no"
          }`}
        >
          {scheduleStatus}
        </span>
      ) : null}
    </p>
  );
};

const StudyColorDot = ({ color, title }) => (
  <span
    className="site-study-row__color"
    style={{ backgroundColor: color || "#94a3b8" }}
    title={title}
    aria-hidden="true"
  />
);

const PIAvatar = ({ name }) => {
  if (!name) return null;

  return (
    <span
      className="site-study-row__pi-avatar"
      style={{ backgroundColor: getAvatarColor(name) }}
      title={name}
      aria-label={name}
    >
      {getInitials(name)}
    </span>
  );
};

const StudyListRow = ({
  row,
  readOnly = false,
  checked = false,
  onToggle,
  onAssignOne,
  onRemoveStudy,
  assigningStudy = false,
  isRemoving = false,
  showScheduleStatus = false,
}) => {
  const name = row.StudyName || "Unknown";

  return (
    <div
      className={`site-study-row${
        readOnly ? " site-study-row--assigned" : " site-study-row--selectable"
      }${checked ? " is-selected" : ""}`}
    >
      {!readOnly && onToggle ? (
        <input
          type="checkbox"
          className="form-check-input site-study-row__checkbox"
          checked={checked}
          onChange={onToggle}
          aria-label={`Select ${name}`}
        />
      ) : null}
      <StudyColorDot color={row.StudyColor} title={name} />
      <div className="site-study-row__main">
        <StudyRowSummary row={row} showScheduleStatus={showScheduleStatus} />
      </div>
      <PIAvatar name={row.PIName} />
      {!readOnly && onAssignOne ? (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary site-study-row__quick-add"
          onClick={onAssignOne}
          disabled={assigningStudy}
          title={`Add ${name}`}
        >
          <i className="fas fa-plus" aria-hidden="true" />
        </button>
      ) : null}
      {!readOnly && onRemoveStudy ? (
        <button
          type="button"
          className="btn btn-sm btn-outline-danger site-study-row__remove"
          onClick={onRemoveStudy}
          disabled={isRemoving || assigningStudy}
          title={`Remove ${name}`}
        >
          {isRemoving ? (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            <i className="fas fa-times" aria-hidden="true" />
          )}
        </button>
      ) : null}
    </div>
  );
};
const SiteStudyAssignmentPanel = ({
  availableStudies = [],
  assignedItems = [],
  onAssignSelected,
  onAssignOne,
  onRemoveStudy,
  assigningStudy = false,
  removingStudyId = "",
  availableTitle = "Available studies",
  availableSubtitle = "Select one or more studies to add to this site.",
  assignedTitle = "Assigned to site",
  assignedSubtitle = "Studies currently linked to this site.",
  availableSearchPlaceholder = "Search available studies...",
  assignedSearchPlaceholder = "Search assigned studies...",
  readOnly = false,
  showScheduleStatus = false,
  className = "",
}) => {
  const [availableSearch, setAvailableSearch] = useState("");
  const [assignedSearch, setAssignedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const filteredAvailable = useMemo(
    () => filterStudyRows(availableStudies, availableSearch),
    [availableStudies, availableSearch],
  );

  const filteredAssigned = useMemo(
    () => filterStudyRows(assignedItems, assignedSearch),
    [assignedItems, assignedSearch],
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allVisibleSelected =
    filteredAvailable.length > 0 &&
    filteredAvailable.every((row) => selectedSet.has(getStudyRowId(row)));

  const toggleStudy = (studyId) => {
    const normalizedId = String(studyId || "").trim();
    if (!normalizedId) return;

    setSelectedIds((prev) =>
      prev.includes(normalizedId)
        ? prev.filter((id) => id !== normalizedId)
        : [...prev, normalizedId],
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(
        filteredAvailable.map((row) => getStudyRowId(row)).filter(Boolean),
      );
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.has(id)));
      return;
    }

    const merged = new Set([
      ...selectedIds,
      ...filteredAvailable.map((row) => getStudyRowId(row)).filter(Boolean),
    ]);
    setSelectedIds([...merged]);
  };

  const handleBulkAdd = () => {
    if (selectedIds.length === 0) return;
    onAssignSelected(selectedIds);
    setSelectedIds([]);
  };

  const resolvedAssignedSubtitle = readOnly
    ? "Studies linked to this site (view only)."
    : assignedSubtitle;

  const assignedEmptyTitle =
    assignedItems.length === 0
      ? readOnly
        ? "No studies assigned"
        : "No studies assigned yet"
      : "No matching studies";

  const assignedEmptyText =
    assignedItems.length === 0
      ? readOnly
        ? "This site has no linked studies."
        : "Select studies on the left and click Add selected."
      : "Try a different search term.";

  return (
    <div
      className={`site-study-assignment-panel${
        readOnly ? " site-study-assignment-panel--read-only" : ""
      } ${className}`.trim()}
    >
      <div
        className={`site-study-split${
          readOnly ? " site-study-split--read-only" : ""
        }`}
      >
        {!readOnly && (
          <section className="site-study-panel site-study-panel--available">
            <div className="site-study-panel__header">
              <div>
                <h6 className="site-study-panel__title">{availableTitle}</h6>
                <p className="site-study-panel__subtitle">{availableSubtitle}</p>
              </div>
              <span className="site-study-panel__count">{availableStudies.length}</span>
            </div>

            <div className="site-study-panel__toolbar">
              <div className="site-study-search site-study-search--full">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  type="text"
                  placeholder={availableSearchPlaceholder}
                  value={availableSearch}
                  onChange={(e) => setAvailableSearch(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="btn btn-primary site-study-bulk-add-btn"
                onClick={handleBulkAdd}
                disabled={assigningStudy || selectedIds.length === 0}
              >
                {assigningStudy ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus me-2" aria-hidden="true" />
                    Add selected ({selectedIds.length})
                  </>
                )}
              </button>
            </div>

            {filteredAvailable.length > 0 && (
              <label className="site-study-select-all">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                />
                <span>Select all shown</span>
              </label>
            )}

            <div className="site-study-list site-study-list--panel">
              {filteredAvailable.length === 0 ? (
                <div className="site-study-empty site-study-empty--compact">
                  <p className="site-study-empty__title">
                    {availableStudies.length === 0
                      ? "No studies available"
                      : "No matching studies"}
                  </p>
                  <p className="site-study-empty__text">
                    {availableStudies.length === 0
                      ? "All eligible studies are already assigned to this site."
                      : "Try a different search term."}
                  </p>
                </div>
              ) : (
                filteredAvailable.map((row) => {
                  const studyId = getStudyRowId(row);
                  return (
                  <StudyListRow
                    key={studyId || row.StudyName}
                    row={row}
                    checked={studyId ? selectedSet.has(studyId) : false}
                    onToggle={
                      studyId ? () => toggleStudy(studyId) : undefined
                    }
                    onAssignOne={
                      studyId ? () => onAssignOne(studyId) : undefined
                    }
                    assigningStudy={assigningStudy}
                    showScheduleStatus={showScheduleStatus}
                  />
                  );
                })
              )}
            </div>
          </section>
        )}

        <section className="site-study-panel site-study-panel--assigned">
          <div className="site-study-panel__header">
            <div>
              <h6 className="site-study-panel__title">{assignedTitle}</h6>
              <p className="site-study-panel__subtitle">{resolvedAssignedSubtitle}</p>
            </div>
            <span className="site-study-panel__count site-study-panel__count--assigned">
              {assignedItems.length}
            </span>
          </div>

          <div className="site-study-panel__toolbar site-study-panel__toolbar--single">
            <div className="site-study-search site-study-search--full">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder={assignedSearchPlaceholder}
                value={assignedSearch}
                onChange={(e) => setAssignedSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="site-study-list site-study-list--panel">
            {filteredAssigned.length === 0 ? (
              <div className="site-study-empty site-study-empty--compact">
                <div className="site-study-empty__icon">
                  <i className="fas fa-book-medical" aria-hidden="true" />
                </div>
                <p className="site-study-empty__title">{assignedEmptyTitle}</p>
                <p className="site-study-empty__text">{assignedEmptyText}</p>
              </div>
            ) : (
              filteredAssigned.map((row) => {
                const studyId = getStudyRowId(row);
                return (
                <StudyListRow
                  key={studyId || row.StudyName}
                  row={row}
                  readOnly={readOnly}
                  onRemoveStudy={
                    !readOnly && studyId
                      ? () => onRemoveStudy(studyId)
                      : undefined
                  }
                  assigningStudy={assigningStudy}
                  isRemoving={studyId ? removingStudyId === studyId : false}
                  showScheduleStatus={showScheduleStatus}
                />
                );
              })
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default SiteStudyAssignmentPanel;
